/** Attention (DESIGN §24.2): at the top of the Week, the few things worth a look, each dismissable in this browser. */
import "./Attention.css";
import { useState } from "react";
import { Link } from "react-router";
import { store, stored } from "../browser.js";
import { useModel } from "../context.js";
import { attentionKey, loadAttention, loadKey, loadLoad, loadPreviousWorkouts, previousWorkoutsKey, useResource } from "../data.js";
import { type AttentionItem, attentionItems, attentionRows } from "../model/attention.js";
import { dayLong, weekdayShort } from "../model/days.js";
import { levelWord } from "../model/effort.js";
import { href } from "../paths.js";
import { BodyLevel } from "./common.js";

export const DISMISSED_KEY = "portal.attention.dismissed";
const KEEP = 300;

function readDismissed(): string[] {
  try {
    const list: unknown = JSON.parse(stored(DISMISSED_KEY) ?? "[]");
    return Array.isArray(list) ? list.filter((x): x is string => typeof x === "string") : [];
  } catch {
    return [];
  }
}

function itemHref(monday: string, item: AttentionItem): string {
  const page = href(monday, "athlete", item.athleteID);
  return item.type === "flag" ? page + "#ath-w-" + item.flag.date : item.type === "body" ? page + "#ath-w-" + item.body.date : page;
}

function summary(item: AttentionItem): string {
  if (item.type === "flag") return "“" + item.flag.quote.trim() + "”";
  if (item.type === "body") return [levelWord(item.body.level), item.body.where, item.body.note ? "“" + item.body.note + "”" : ""].filter(Boolean).join(" · ");
  return item.text;
}

function Day({ date }: { date: string }) {
  return (
    <span className="att-date" title={dayLong(date)}>
      <span aria-hidden="true">{weekdayShort(date)}</span>
      <span className="sr">{dayLong(date)}</span>
    </span>
  );
}

function Coming({ coming }: { coming: { date: string; name: string }[] }) {
  return coming.length ? <span className="att-coming">Still this week: {coming.map((c) => c.name + " " + weekdayShort(c.date)).join(" · ")}</span> : null;
}

export function Attention() {
  const model = useModel();
  const flags = useResource(attentionKey(model.monday), () => loadAttention(model.monday)).value;
  const load = useResource(loadKey, loadLoad).value;
  const previous = useResource(previousWorkoutsKey(model.monday), () => loadPreviousWorkouts(model.monday)).value;
  const [dismissed, setDismissed] = useState(readDismissed);
  const rows = attentionRows(attentionItems({ model, flags, load, previous }), new Set(dismissed));
  if (!rows.length) return null;

  const dismiss = (key: string) => {
    const next = [...readDismissed().filter((k) => k !== key), key].slice(-KEEP);
    store(DISMISSED_KEY, JSON.stringify(next));
    setDismissed(next);
  };

  return (
    <section className="att" aria-labelledby="att-h">
      <h2 id="att-h">Attention</h2>
      <ul className="att-list">
        {rows.map((item) => (
          <li key={item.key} className={"att-row is-" + (item.type === "flag" ? "flag" : item.type === "body" ? item.body.level : item.kind)}>
            <Link className="att-link" to={itemHref(model.monday, item)}>
              <span className="att-line">
                <span className="att-name">{item.name}</span>
                {item.type === "flag" ? (
                  <>
                    <span className="att-quote">“{item.flag.quote.trim()}”</span>
                    <Day date={item.flag.date} />
                  </>
                ) : item.type === "body" ? (
                  <>
                    <BodyLevel level={item.body.level} />
                    {item.body.where ? <span className="att-where">{item.body.where}</span> : null}
                    {item.body.note ? <span className="att-quote">“{item.body.note}”</span> : null}
                    <Day date={item.body.date} />
                  </>
                ) : (
                  <span className="att-text">{item.text}</span>
                )}
              </span>
              {item.type === "flag" ? (
                <>
                  <Coming coming={item.coming} />
                  <span className="att-caption">From their notes</span>
                </>
              ) : item.type === "body" ? (
                <Coming coming={item.coming} />
              ) : null}
            </Link>
            <button type="button" className="k-close att-dismiss" aria-label={"Dismiss " + item.name + ": " + summary(item)} title="Dismiss" onClick={() => dismiss(item.key)}>
              <svg viewBox="0 0 14 14" aria-hidden="true">
                <path d="M3 3l8 8M11 3l-8 8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
              </svg>
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}
